// components/secure/pm/ScreeningPanel.js — screening one applicant, in sequence.
//
// The screen enforces what the law enforces: the criminal-history step does not
// appear until the applicant is prequalified AND holds a conditional offer, and
// it says why rather than hiding a disabled button. Cook County Just Housing
// Amendment, § 42-38.
//
// The income line always shows its arithmetic. Where a voucher pays part of the
// rent, the tenant's portion is what the multiple applies to — Illinois Human
// Rights Act, source of income — and a leasing agent should be able to see that
// happen rather than trust that it did.
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import { Panel, Pill, Field, Input, Select, Textarea, Button, Notice, money, fmtDate } from './PMKit';
import BusinessUnderwriting from './BusinessUnderwriting';
import { BusinessDecision, BusinessDecided, ApplicationDocuments } from './BusinessScreening';

const STEPS = [
    ['prequalification', 'Prequalify'],
    ['conditional_offer', 'Conditional offer'],
    ['criminal_review', 'Criminal review'],
    ['decided', 'Decision'],
];

const StepRail = ({ stage, prequalified }) => {
    const idx = STEPS.findIndex(([k]) => k === (stage || 'prequalification'));
    return (
        <ol className="mb-4 flex flex-wrap gap-2 text-xs">
            {STEPS.map(([key, label], i) => {
                const done = i < idx || (i === idx && stage === 'decided');
                const here = i === idx;
                const blocked = key === 'criminal_review' && !prequalified;
                return (
                    <li key={key}
                        className={`rounded px-2 py-1 ${
                            done ? 'bg-emerald-100 text-emerald-800'
                                : here ? 'bg-slate-800 text-white'
                                : blocked ? 'bg-slate-100 text-slate-400'
                                : 'bg-slate-100 text-slate-500'}`}>
                        {i + 1}. {label}
                    </li>
                );
            })}
        </ol>
    );
};

const ScreeningPanel = ({ applicationId, onChanged }) => {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const apiClient = useApiClient();

    const load = useCallback(async () => {
        try {
            const d = await apiClient.get(`/api/screening/applications/${applicationId}`);
            setData(d);
            setForm((f) => ({ monthly_rent: d.screening?.monthly_rent || '',
                              subsidy_amount: d.screening?.subsidy_amount || '',
                              verified_monthly_income: d.screening?.verified_monthly_income || '',
                              credit_score: d.screening?.credit_score || '',
                              rental_history_result: d.screening?.rental_history_result || 'clear', ...f }));
        } catch (e) { setErr(e); }
    }, [apiClient, applicationId]);
    useEffect(() => { load(); }, [load]);

    const post = async (path, body) => {
        setBusy(true); setErr(null);
        try {
            const r = await apiClient.post(`/api/screening/applications/${applicationId}/${path}`, body);
            await load(); onChanged?.();
            return r;
        } catch (e) { setErr(e); return null; } finally { setBusy(false); }
    };

    if (!data) return <Panel title="Screening"><p className="text-sm text-slate-500">Loading…</p></Panel>;

    const { screening: s, criteria: c, application: app } = data;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    // The housing sequence belongs to housing. A business tenancy is underwritten
    // in one pass, and imposing the Cook County staging on it would be inventing
    // a restriction that does not exist.
    const isHousing = (app?.asset_class || 'residential') === 'residential';

    if (!c) {
        return (
            <Panel title="Screening">
                <Notice tone="bad" title="No published criteria"
                    message="No screening criteria are in force for this property. Publish a version before screening
                             anyone — uniform written criteria applied to every applicant alike is the fair housing
                             defence, and criteria written after a complaint is not." />
            </Panel>
        );
    }

    // Live preview of the income arithmetic, so the agent sees the subtraction.
    const rent = Number(form.monthly_rent) || 0;
    const subsidy = Number(form.subsidy_amount) || 0;
    const portion = Math.max(0, rent - subsidy);
    const required = portion * Number(c.income_multiple || 0);

    return (
        <Panel title="Screening"
               subtitle={isHousing
                   ? `Criteria ${c.version} · ${c.income_multiple}x income · credit ${c.min_credit_score || '—'}`
                   : `Criteria ${c.version} · ${c.rent_coverage_ratio}x rent coverage · PAYDEX ${c.min_paydex || '—'}`
                     + `${c.environmental_review_required ? ' · environmental review' : ''}`}>
            {err && <Notice tone="bad" title="Refused" message={err.message} />}

            {!isHousing && (
                <>
                    <BusinessUnderwriting applicationId={applicationId} application={app} criteria={c}
                                          screening={s} business={data.business}
                                          assessed={{ underwriting: data.underwriting, environmental: data.environmental }}
                                          onDone={() => { load(); onChanged?.(); }} />
                    {s?.stage === 'decided'
                        ? <BusinessDecided applicationId={applicationId} application={app} screening={s}
                                           business={data.business} onDone={() => { load(); onChanged?.(); }} />
                        : <BusinessDecision key={data.business?.underwritten_on || 'none'} applicationId={applicationId}
                                            application={app} screening={s} business={data.business}
                                            assessed={{ underwriting: data.underwriting, environmental: data.environmental }}
                                            onDone={() => { load(); onChanged?.(); }} />}
                    <ApplicationDocuments applicationId={applicationId} documents={data.documents} />
                </>
            )}

            {isHousing && <StepRail stage={s?.stage} prequalified={!!s?.prequalified} />}

            {/* ---------------- step 1 ---------------- */}
            {isHousing && (!s || s.stage === 'prequalification') && (
                <div className="space-y-3">
                    <div className="grid gap-x-4 sm:grid-cols-3">
                        <Input label="Monthly rent" value={form.monthly_rent} onChange={set('monthly_rent')} />
                        <Input label="Voucher / subsidy portion" value={form.subsidy_amount}
                               onChange={set('subsidy_amount')}
                               hint="Third-party paid share, if any" />
                        <Input label="Verified monthly income" value={form.verified_monthly_income}
                               onChange={set('verified_monthly_income')} />
                    </div>
                    {rent > 0 && (
                        <p className={`rounded px-3 py-2 text-sm ${subsidy > 0 ? 'bg-amber-50 text-amber-900' : 'bg-slate-50 text-slate-700'}`}>
                            {subsidy > 0
                                ? <>Tenant&apos;s portion {money(portion)} of {money(rent)} rent · income required{' '}
                                   <strong>{money(required)}</strong> — the multiple applies to the tenant&apos;s
                                   portion only (Illinois Human Rights Act, source of income).</>
                                : <>Income required <strong>{money(required)}</strong> ({c.income_multiple}x {money(rent)}).</>}
                        </p>
                    )}
                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <Input label="Credit score" value={form.credit_score} onChange={set('credit_score')} />
                        <Select label="Rental history" value={form.rental_history_result}
                                onChange={set('rental_history_result')}
                                options={[{ value: 'clear', label: 'Clear' }, { value: 'concerns', label: 'Concerns' },
                                          { value: 'adverse', label: 'Adverse' }, { value: 'none', label: 'No history' }]} />
                    </div>
                    <p className="text-xs text-slate-500">
                        Criminal history is not asked for, obtained or considered at this step, and the field to
                        record it does not exist yet. It appears only after a conditional offer.
                    </p>
                    <Button disabled={busy} onClick={() => post('prequalify', {
                        monthly_rent: form.monthly_rent, subsidy_amount: form.subsidy_amount || 0,
                        verified_monthly_income: form.verified_monthly_income,
                        credit_score: form.credit_score || null,
                        rental_history_result: form.rental_history_result,
                    })}>Run prequalification</Button>
                </div>
            )}

            {/* ---------------- result of step 1 ---------------- */}
            {isHousing && s && s.prequalified != null && (
                <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Pill tone={s.prequalified ? 'good' : 'bad'}>
                            {s.prequalified ? 'Prequalified' : 'Not prequalified'}
                        </Pill>
                        {s.prequalified_on && <span className="text-xs text-slate-400">{fmtDate(s.prequalified_on)}</span>}
                    </div>
                    <div className="grid gap-x-6 sm:grid-cols-3">
                        <Field label="Tenant portion" value={money(s.tenant_portion_rent)} />
                        <Field label="Income ratio" value={s.income_ratio ? `${s.income_ratio}x` : '—'} />
                        <Field label="Credit" value={s.credit_score || '—'} />
                    </div>
                    {Array.isArray(s.prequalification_failures) && s.prequalification_failures.length > 0 && (
                        <ul className="list-disc space-y-1 pl-5 text-sm text-rose-700">
                            {s.prequalification_failures.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                    )}
                </div>
            )}

            {/* ---------------- step 2 ---------------- */}
            {isHousing && s?.prequalified && !s.conditional_offer_on && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="mb-2 text-sm text-slate-600">
                        A conditional offer comes next. It is what opens the criminal-history step, and nothing about
                        criminal history may be sought before it.
                    </p>
                    <Button disabled={busy} onClick={() => post('conditional-offer', {})}>
                        Record conditional offer
                    </Button>
                </div>
            )}

            {/* ---------------- step 3 ---------------- */}
            {isHousing && s?.conditional_offer_on && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                    <p className="mb-2 text-xs text-slate-500">
                        Conditional offer {fmtDate(s.conditional_offer_on)} · only convictions disposed within the last{' '}
                        {c.criminal_lookback_years} years may be considered · a denial on this basis needs every factor below.
                    </p>
                    <div className="space-y-2">
                        <Textarea label="Nature and severity of the offense" rows={2}
                                  value={form.ia_nature_and_severity || s.ia_nature_and_severity || ''}
                                  onChange={set('ia_nature_and_severity')} />
                        <Textarea label="Time elapsed since the offense" rows={2}
                                  value={form.ia_time_since_offense || s.ia_time_since_offense || ''}
                                  onChange={set('ia_time_since_offense')} />
                        <Textarea label="Evidence of rehabilitation or mitigating circumstances" rows={2}
                                  value={form.ia_rehabilitation_evidence || s.ia_rehabilitation_evidence || ''}
                                  onChange={set('ia_rehabilitation_evidence')} />
                        <Textarea label="Relevance to this tenancy" rows={2}
                                  value={form.ia_tenancy_relevance || s.ia_tenancy_relevance || ''}
                                  onChange={set('ia_tenancy_relevance')} />
                        <div className="grid gap-x-4 sm:grid-cols-2">
                            <Input label="Applicant notified on" type="date"
                                   value={form.applicant_notified_on || s.applicant_notified_on?.slice(0, 10) || ''}
                                   onChange={set('applicant_notified_on')} />
                            <Input label="Response due" type="date"
                                   value={form.applicant_response_due || s.applicant_response_due?.slice(0, 10) || ''}
                                   onChange={set('applicant_response_due')} />
                        </div>
                        <Button variant="ghost" disabled={busy} onClick={() => post('criminal-review', {
                            ia_nature_and_severity: form.ia_nature_and_severity,
                            ia_time_since_offense: form.ia_time_since_offense,
                            ia_rehabilitation_evidence: form.ia_rehabilitation_evidence,
                            ia_tenancy_relevance: form.ia_tenancy_relevance,
                            applicant_notified_on: form.applicant_notified_on || null,
                            applicant_response_due: form.applicant_response_due || null,
                            ia_completed_on: new Date().toISOString().slice(0, 10),
                        })}>Save individualized assessment</Button>
                    </div>
                </div>
            )}

            {/* ---------------- step 4 ---------------- */}
            {isHousing && s && s.stage !== 'decided' && s.prequalified != null && (
                <div className="mt-4 border-t border-slate-100 pt-3 space-y-2">
                    <div className="grid gap-x-4 sm:grid-cols-2">
                        <Select label="Outcome" value={form.outcome || ''} onChange={set('outcome')}
                                options={[{ value: '', label: 'Select…' },
                                          { value: 'approved', label: 'Approved' },
                                          { value: 'approved_with_conditions', label: 'Approved with conditions' },
                                          { value: 'declined', label: 'Declined' },
                                          { value: 'withdrawn', label: 'Withdrawn' }]} />
                        <Input label="Reason" value={form.outcome_reason || ''} onChange={set('outcome_reason')} />
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <label className="flex items-center gap-1">
                            <input type="checkbox" checked={!!form.consumer_report_used}
                                   onChange={(e) => setForm({ ...form, consumer_report_used: e.target.checked })} />
                            A consumer report was relied on
                        </label>
                        {/* Housing only: the individualized-assessment duty this triggers
                            is the Cook County ordinance, which does not reach a business tenant. */}
                        {isHousing && (
                            <label className="flex items-center gap-1">
                                <input type="checkbox" checked={!!form.based_on_criminal_history}
                                       onChange={(e) => setForm({ ...form, based_on_criminal_history: e.target.checked })} />
                                Based on criminal history
                            </label>
                        )}
                    </div>
                    <Button disabled={busy || !form.outcome} onClick={() => post('decide', {
                        outcome: form.outcome, outcome_reason: form.outcome_reason,
                        consumer_report_used: !!form.consumer_report_used,
                        based_on_criminal_history: isHousing && !!form.based_on_criminal_history,
                        screening_vendor: form.screening_vendor || null,
                        reporting_agency: form.reporting_agency || null,
                    })}>Record decision</Button>
                </div>
            )}

            {isHousing && s?.stage === 'decided' && (
                <div className="mt-4 border-t border-slate-100 pt-3">
                    <Pill tone={s.outcome === 'declined' ? 'bad' : 'good'}>{s.outcome?.replace(/_/g, ' ')}</Pill>
                    <span className="ml-2 text-xs text-slate-400">{fmtDate(s.decided_on)}</span>
                    {s.outcome_reason && <p className="mt-1 text-sm text-slate-600">{s.outcome_reason}</p>}
                    {data.application?.adverse_action_required === 1 && !data.application?.adverse_action_sent_on && (
                        <Notice tone="bad" title="FCRA adverse action notice is owed"
                            message="A consumer report was relied on. The applicant is entitled to a notice naming the
                                     agency, stating the agency did not make the decision, and setting out the right to a
                                     free copy and to dispute it. FCRA § 615(a)." />
                    )}
                </div>
            )}
        </Panel>
    );
};

export default ScreeningPanel;
